import type { AnyFormApi } from '@tanstack/react-form'
import { isValid, parseISO } from 'date-fns'

import type {
  ExtendedFieldValue,
  ExtendedSchemaDerivedField,
  JsonValue,
} from '@/types/extended-schema'

export const ISO_DATE_REGEX =
  /^(\d{4}-\d{2}-\d{2})(T\d{2}:\d{2}(:\d{2})?)?(Z|[+-]\d{2}:?\d{2})?$/

// Stored values are untyped JSON: a legacy field once read as text can hold "", "1500000" or "$1,500,000".
const NUMERIC_TEXT = /^[-+]?\$?(\d+|\d{1,3}(,\d{3})+)(\.\d+)?([eE][-+]?\d+)?$/

export const parseStoredNumber = (value: unknown): number | null => {
  const number =
    typeof value === 'string' && NUMERIC_TEXT.test(value.trim())
      ? Number(value.trim().replace(/[$,]/g, ''))
      : value
  return typeof number === 'number' && Number.isFinite(number) ? number : null
}

const BOOLEAN_WORDS = new Map([
  ['true', true],
  ['yes', true],
  ['false', false],
  ['no', false],
])

export const parseStoredBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return null
  return BOOLEAN_WORDS.get(value.trim().toLowerCase()) ?? null
}

export const getStoredExtendedValues = (
  data: unknown,
): Readonly<Record<string, JsonValue>> =>
  data && typeof data === 'object' && !Array.isArray(data)
    ? (data as Record<string, JsonValue>)
    : {}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'number')

// Mirrors the validators: a field without options accepts any text.
const isOption = (options: readonly string[], value: string): boolean =>
  options.length === 0 || options.includes(value)

export function readStoredFieldValue(
  field: ExtendedSchemaDerivedField,
  stored: unknown,
): ExtendedFieldValue | undefined {
  switch (field.type) {
    case 'string':
      return typeof stored === 'string' ? stored : undefined
    case 'number':
    case 'currency':
      return parseStoredNumber(stored) ?? undefined
    case 'boolean':
      return parseStoredBoolean(stored) ?? undefined
    case 'dropdown':
      return typeof stored === 'string' && isOption(field.options ?? [], stored)
        ? stored
        : undefined
    case 'multi_select':
      return isStringArray(stored)
        ? stored.filter((item) => isOption(field.options ?? [], item))
        : undefined
    case 'string_list':
      return isStringArray(stored) ? stored : undefined
    case 'number_list':
      return isNumberArray(stored) ? stored : undefined
    case 'date':
      return typeof stored === 'string' &&
        ISO_DATE_REGEX.test(stored) &&
        isValid(parseISO(stored))
        ? stored
        : undefined
    default:
      return undefined
  }
}

// Saving keeps untouched text the field's type can't read, like "TBD" in a currency field, so validation skips it.
export const keepsLegacyText = (
  field: ExtendedSchemaDerivedField,
  storedValues: { readonly [fieldId: string]: unknown },
  isEdited: (fieldId: string) => boolean,
): boolean => {
  const stored = storedValues[field.id]
  return (
    !isEdited(field.id) &&
    typeof stored === 'string' &&
    stored.trim() !== '' &&
    readStoredFieldValue(field, stored) === undefined
  )
}

// Fields the user never edited keep their stored value, so unreadable text survives a save.
export function mergeEditedExtendedValues<Stored, Edited>(
  storedValues: { readonly [fieldId: string]: Stored },
  submittedValues: { readonly [fieldId: string]: Edited },
  isEdited: (fieldId: string) => boolean,
): Record<string, Stored | Edited> {
  const values: Record<string, Stored | Edited> = {}
  for (const [fieldId, value] of Object.entries(submittedValues)) {
    values[fieldId] =
      !isEdited(fieldId) && Object.hasOwn(storedValues, fieldId)
        ? storedValues[fieldId]
        : value
  }
  return values
}

// Both edit dialogs register extended fields as `values.<fieldId>`.
export const isExtendedFieldEdited =
  (formApi: AnyFormApi) =>
  (fieldId: string): boolean =>
    formApi.getFieldMeta(`values.${fieldId}`)?.isDirty ?? false

export const getSearchableTextFromValue = (
  value: JsonValue | undefined,
): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return value.toString()
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string' || typeof item === 'number'
          ? item.toString()
          : '',
      )
      .join(' ')
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}
